<template>
  <div id="trailHub" v-if="trailsDetails.name">
    <h2 class="mt-2">Trail Reports<br>
      <small>
        <b-btn
          @click="showMap = true"
          variant="secondary"
          size="sm">
          <i class="fas fa-map"></i> View Map
        </b-btn>
      </small>
    </h2>
    <div class="row">

      <!-- Trails -->
      <div class="col-sm-3">
        <div class="card">
          <div class="card-body">
            <div class="card-icon">
              <i class="fas fa-map-signs"></i>
            </div><!-- /.card-icon -->
            <div class="card-title">
              <p>Trail Stats</p>
            </div><!-- /.card-title -->
            <div v-for="activity in trailsDetails.activities" class="card p-1 mb-1">
              <div class="card-figure-label">
                <strong><i :class="activity.iconClass"></i> {{ activity.en }}</strong><br>
                <i class="fas fa-check-circle text-success"></i> {{ activity.openDistance | trimNumber }} km ({{ (activity.openDistance / activity.totalDistance) * 100 | trimNumber }}%)<br>
                <i class="fas fa-exclamation-circle text-warning"></i> {{ activity.cautionDistance | trimNumber }} km ({{ (activity.cautionDistance / activity.totalDistance) * 100 | trimNumber }}%)</br>
                <i class="fas fa-times-circle text-danger"></i> {{ activity.closedDistance | trimNumber }} km ({{ (activity.closedDistance / activity.totalDistance) * 100 | trimNumber }}%)</br>
                <p class="mb-0 pb-0 text-right"><a :href="'https://trailhub.org/ts/AEdpYWaM2JbnaTxL4kwm?a=' + activity.value" target="_blank" class="text-secondary"><i class="fas fa-map"></i> View on Map ></a></p>
              </div><!-- /..card-figure-label -->
            </div><!-- /.activity in activities -->
          </div><!-- /.card-body -->
        </div>
      </div><!-- /.col-sm-3 -->

      <!-- Open -->
      <div class="col-sm-9" v-if="trailsDetails.updates.notice">
        <div class="card">
          <div class="card-body">
            <div class="card-icon">
              <i class="fas fa-bullhorn"></i>
            </div><!-- /.card-icon -->
            <div class="card-title">
              <p>Right Now</p>
            </div><!-- /.card-title -->
            <div
              v-for="(notice, i) in trailsDetails.updates.notice"
              v-bind:key="i"
            >
              <div id="notice" class="alert mb-2" :class="'alert-' + notice.severity">
                <p class="mb-0"><i v-if="notice.severity === 'info'" class="fas fa-info-circle"></i><i v-if="notice.severity === 'warning' || notice.severity === 'danger'" class="fas fa-exclamation-circle"></i> {{ notice.description }} <small><em>~Updated: {{ notice.updatedOn | toDate }}</em></small></p>
                <b-btn
                  v-if="notice.linkUrl"
                  :href="notice.linkUrl"
                  :class="'btn-' + notice.severity"
                  class="mt-2"
                  target="_blank"
                  size="sm"
                >
                  Learn More
                </b-btn>
              </div><!-- /.alert -->
            </div><!-- /..card-figure-label -->
          </div><!-- /.card-body -->
        </div>
      </div><!-- /.col-sm-3 -->

    </div><!-- /.row -->

    <div class="row mt-2" v-if="trailsDetails.updates.snowReport">
      <!-- Snowfall -->
      <div class="col-sm-12">
        <div class="card" v-for="(snowReport, i) in trailsDetails.updates.snowReport">
          <div class="card-body">
            <div class="card-icon">
              <i class="fas fa-snowflake"></i>
            </div><!-- /.card-icon -->
            <div class="card-title">
              <p>Snowfall</p>
            </div><!-- /.card-title -->
            <div class="row">
              <div class="col">
                <div class="card-figure">
                  {{ snowReport.twentyFourHours }}"
                </div><!-- /.card-figure -->
                <div class="card-figure-label">
                  Last 24 Hrs.
                </div><!-- /..card-figure-label -->
              </div>
              <div class="col">
                <div class="card-figure">
                  {{ snowReport.fourtyEightHours }}"
                </div><!-- /.card-figure -->
                <div class="card-figure-label">
                  Last 48 Hrs.
                </div><!-- /..card-figure-label -->
              </div>
              <div class="col">
                <div class="card-figure">
                  {{ snowReport.seasonTotal }}"
                </div><!-- /.card-figure -->
                <div class="card-figure-label">
                  All Season
                </div><!-- /..card-figure-label -->
              </div>
            </div><!-- /.row -->
            <p class="mb-0 mt-2 text-right"><small><i>~Updated: {{ snowReport.updatedOn | toDate }}</i></small></p>
          </div><!-- /.card-body -->
        </div>
      </div><!-- /.col-sm-6 -->
    </div><!-- /.row -->

    <!-- Current Trail Conditions -->
    <div class="row">
      <div class="col-md-12">
        <div class="card mt-2 table-responsive">
          <h3 class="p-2 text-center"><i :class="selectedActivity.iconClass"></i> {{ selectedActivity.label }} Trails</h3>
          <ul class="nav nav-pills p-2" v-if="trailsDetails.activities.length > 1">
            <li
              class="nav-item"
              v-for="(activity, i) in trailsDetails.activities"
            >
              <button
                class="nav-link btn btn-outline-dark mr-2 mb-1"
                :class="{ active: selectedActivity.value === activity.value }"
                @click="changeActivity(activity)"
              >
                <i :class="activity.iconClass"></i> <span>{{ activity.label }}</span>
              </button>
            </li>
          </ul>
          <table class="table">
            <thead>
              <tr>
                <th scope="col">Status</th>
                <th scope="col">Rating</th>
                <th scope="col">Trail</th>
                <th scope="col">Length</th>
                <th scope="col">Notes</th>
              </tr>
            </thead>
            <tbody v-if="trails.length > 0">
              <tr v-for="trail in trailsInView" >
                <td v-if="trail.properties.activityStatus" v-html="$options.filters.toFa(trail.properties.activityStatus)"></td>
                <td v-else v-html="$options.filters.toFa(trail.properties.status)"></td>
                <td v-html="$options.filters.toDiff(trail.properties.difficulty)"></td>
                <td>{{ trail.properties.name }}</td>
                <td v-if="trail.properties.distance">{{ trail.properties.distance | trimNumberDec }} km.</td>
                <td>
                  <span v-if="trail.properties.updates.notice"><span v-for="(notice, i) in trail.properties.updates.notice" :class="'text-' + notice.severity"><i :class="notice.iconClass"></i> {{ notice.description }} <small><em>~{{ notice.updatedOn | toDate }}</em></small><br></span></span>
                  <span v-if="trail.properties.updates.surfaceConditions"><span v-for="(condition, i) in trail.properties.updates.surfaceConditions[0].conditions" class="badge badge-pill badge-dark mr-1"><i :class="condition.iconClass"></i> {{ condition.en }}</span><small><em>~{{ trail.properties.updates.surfaceConditions[0].updatedOn | toDate }}</em></small></br></span>
                  {{ trail.properties.description }}
                </td>
              </tr>
            </tbody>
          </table>
        </div><!-- /.card -->
      </div><!-- /.col-md-12 -->
    </div><!-- /.row -->
    <!-- Trails -->
    <div id="trailMap" v-if="showMap">
      <div class="card h-100">
        <div class="card-body h-100">
          <div class="card-icon">
            <i class="fas fa-times clickable" @click="showMap = false"></i>
          </div><!-- /.card-icon -->
          <div class="card-title">
            <p>Trail Map</p>
          </div><!-- /.card-title -->
          <iframe width="100%" height="95%" src="https://trailhub.org/ts/AEdpYWaM2JbnaTxL4kwm/map/embed" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>
        </div><!-- /.card-body -->
      </div>
    </div><!-- /.col-sm-3 -->
  </div>
</template>

<script>
  import axios from 'axios'
  import moment from 'moment'
  import { mapState } from 'vuex'

  export default {
    data() {
      return {
        showMap: false,
        trailsInView: [],
        trails: [],
        trailsDetails: {},
        // selectedActivity: {
        //   label: 'Nordic Skiing',
        //   value: 'nordicSkiing',
        //   iconClass: 'fas fa-skiing-nordic'
        // },
        selectedActivity: {
          label: 'Mountain Biking',
          value: 'mountainBiking',
          iconClass: 'fas fa-biking-mountain'
        }
      }
    },
    computed: {
      ...mapState(['dashboard'])
    },
    mounted () {
      let self = this
      setTimeout(() => {
        let timeStamp = new Date().getTime()
        let defaultActivity = self.dashboard.data.modules.trailHub.defaultActivity
        let orgAPI = self.dashboard.data.modules.trailHub.orgAPIAccessKey
        let locAPI = self.dashboard.data.modules.trailHub.locationActivityAPIKey
        let tsID = self.dashboard.data.modules.trailHub.tsID
        // let tsID = 'cYP94vnQ55aRe8iKTQkY' // GUNNER GROVE ID
        // axios.get('https://trailhub.org/api/trails/' + tsID + '?timestamp=' + timeStamp).then(response => {
        //   self.trails = response.data
        //   console.log(self.trails)
        // })
        axios.get('https://trailhub.org/api/ts/' + tsID + '?timestamp=' + timeStamp).then(response => {
          self.trailsDetails = response.data
          // Remove hidden activities
          self.trailsDetails.hiddenActivities.forEach(hiddenActivity => {
            self.trailsDetails.activities.forEach((activity, i) => {
              if (hiddenActivity.value == activity.value) {
                self.trailsDetails.activities.splice(i, 1)
              }
            })
          })
          console.log(self.trailsDetails)
          axios.get(self.trailsDetails.geoJsonUrl).then(response => {
            self.trails = []
            const menus = []
            console.log(response.data)
            response.data.features.forEach(feature => {
              if (feature.properties.showInLists && !feature.properties.hidden) {
                self.trails.push(feature)
              } else if (feature.properties.diningOptions) {
                menus.push(feature)
              }
            })
            if (menus.length > 0) {
              this.$store.commit('setStateProperty', { property: 'diningOptions', value: menus })
            }
            this.changeActivity(defaultActivity)
          })
        })
      }, 1000)
    },
    methods: {
      changeActivity (activity) {
        this.selectedActivity = activity
        this.trailsInView = []
        this.trails.forEach((trail, i) => {
          trail.properties.activities.forEach(activity => {
            if (activity.value === this.selectedActivity.value) {
              trail.properties.activityStatus = activity.status
              this.trailsInView.push(trail)
            }
          })
        })
      }
    },
    filters: {
      toDiff (val) {
        if (val === 'circle') {
          return '<span class="dot Easy"></span>'
        }
        if (val === 'square') {
          return '<span class="dot Intermediate"></span>'
        }
        if (val === 'diamond' || val === 'doubleDiamond') {
          return '<span class="dot Expert"></span>'
        }
      },
      toDate (val) {
        if (!val) { return '-' }
        var secs = val._seconds
        var t = new Date(secs * 1000)
        return moment(t).format('MM/DD/YY - hh:mm a')
      },
      trimNumber (value) {
        return value.toFixed(0)
      },
      trimNumberDec (value) {
        return value.toFixed(1)
      },
      toFa(value) {
        if (value == "Closed") {
          let fa = '<i style="color:red;" class="fas fa-times-circle"></i>'
          return fa
        }
        if (value == "Caution") {
          let fa = '<i style="color:orange;" class="fas fa-exclamation-circle"></i>'
          return fa
        }
        if (value === "Open") {
          let fa = '<i style="color:green;" class="fas fa-check-circle"></i>'
          return fa
        }
      }
    }
  }
</script>

<style scoped>
#trailMap {
  position: fixed;
  top: 0;
  width: 100%;
  left: 0;
  height: 100%;
  padding: 20px;
  background-color: rgba(0,0,0,0.8);
}
</style>



// WEBPACK FOOTER //
// src/components/TrailHub.vue